'use client'

import {
  ThemeProvider as CleanLabThemeProvider,
  Toaster,
  TooltipProvider,
} from '@cleanlab/design-system/components'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { ThemeProvider } from 'next-themes'
import { NuqsAdapter } from 'nuqs/adapters/next/app'

import { SideNav } from '@/components/SideNav/SideNav'
import { TopLevelCodexNotification } from '@/components/TopLevelCodexNotification/TopLevelCodexNotification'
import { TopNav } from '@/components/TopNav/TopNav'
import { ERROR_DOM_ID } from '@/global/consts'
import { AccountVerificationGuard } from '@/providers/account/AccountVerificationGuard'
import { UserContextProvider } from '@/providers/account/UserContextProvider'

import DatadogInit from './DatadogInit'
import { MSWProvider } from './msw-provider'
import { AnalyticsProvider } from './providers/analytics/AnalyticsProvider'
import { AccessTokenProvider } from './providers/authentication/AccessTokenProvider'
import { Auth0ProviderWithHistory } from './providers/authentication/Auth0ProviderWithHistory'
import { AuthenticationGuard } from './providers/authentication/AuthenticationGuard'

const NO_RETRY_STATUS_CODES = [400, 404, 422]

// Create QueryClient instance outside of component
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      retry: (failureCount, error) => {
        const status = error instanceof AxiosError ? error.response?.status : undefined
        // Don't retry 400, 404 or 422 errors so we can show the not-found page
        // immediately. These are all error types that should expect that
        // repeating the request without modification will fail with the same
        // error.
        if (status && NO_RETRY_STATUS_CODES.includes(status)) {
          return false
        }
        return failureCount < 3
      },
    },
  },
})

export const AppLayout = ({ children }: { children: React.ReactNode }) => {
  return (
    <>
      <Toaster />
      <div className="flex h-screen flex-col">
        <TopNav />
        <div className="flex grow flex-row overflow-hidden">
          <SideNav />
          <div className="h-full grow overflow-y-auto">
            <div id={ERROR_DOM_ID} />
            <TopLevelCodexNotification />
            <AccountVerificationGuard>{children}</AccountVerificationGuard>
          </div>
        </div>
      </div>
    </>
  )
}

export function ClientProviders({ children }: { children: React.ReactNode }) {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider
        themes={['light', 'dark']}
        attribute="data-theme"
        defaultTheme="system"
        enableSystem
      >
        <CleanLabThemeProvider>
          <Auth0ProviderWithHistory>
            <AccessTokenProvider>
              <AuthenticationGuard>
                <UserContextProvider>
                  <DatadogInit />
                  <MSWProvider>
                    <AnalyticsProvider>
                      <TooltipProvider>
                        <NuqsAdapter>{children}</NuqsAdapter>
                      </TooltipProvider>
                    </AnalyticsProvider>
                  </MSWProvider>
                </UserContextProvider>
              </AuthenticationGuard>
            </AccessTokenProvider>
          </Auth0ProviderWithHistory>
        </CleanLabThemeProvider>
      </ThemeProvider>
    </QueryClientProvider>
  )
}
